import { fg } from '@atlassian/jira-feature-gating';
import type { AdditionalFieldValue } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import {
	PRIORITY_TYPE,
	ASSIGNEE_TYPE,
	STATUS_TYPE,
	SPRINT_TYPE,
	NUMBER_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	CATEGORY_TYPE,
	LABELS_TYPE,
	ISSUE_TYPE,
	GROUP_CF_TYPE,
	MULTI_GROUP_CF_TYPE,
	SELECT_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	FIX_VERSIONS_TYPE,
	AFFECTS_VERSIONS_TYPE,
	VERSION_CF_TYPE,
	MULTI_VERSION_CF_TYPE,
	REPORTER_TYPE,
	USER_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { JiraIssueFieldsInput } from '@atlassian/jira-relay/src/__generated__/issueCreateMutation_useIssueCreateMutation.graphql.ts';
import type {
	OptimisticFieldSet,
	OptionFieldType,
	UserFieldType,
	VersionFieldType,
} from './optimistic-responses/types.tsx';
import type {
	GroupContextField,
	GroupContextFieldOld,
	NumberFieldType,
	StringFieldType,
	StringGroupContextField,
} from './types.tsx';
import {
	toOptimisticPriority,
	toOptimisticUser,
	toOptimisticStatus,
	toOptimisticSingleSelect,
	toOptimisticSprint,
	toOptimisticNumber,
	toOptimisticLabels,
	toOptimisticMultiGroup,
	toOptimisticMultiSelect,
	toOptimisticCheckbox,
	toOptimisticRadioSelect,
	toOptimisticMultiVersion,
} from './optimistic-responses/index.tsx';

const OPTION_FIELD_TYPES = new Set<string>([
	CATEGORY_TYPE,
	SELECT_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
]);

const VERSION_FIELD_TYPES = new Set<string>([
	VERSION_CF_TYPE,
	MULTI_VERSION_CF_TYPE,
	AFFECTS_VERSIONS_TYPE,
	FIX_VERSIONS_TYPE,
]);

const USER_FIELD_TYPES = new Set<string>([ASSIGNEE_TYPE, REPORTER_TYPE, USER_CF_TYPE]);

export const STRING_FIELD_TYPES = new Set<string>([
	PRIORITY_TYPE,
	STATUS_TYPE,
	LABELS_TYPE,
	SPRINT_TYPE,
	ISSUE_TYPE,
	GROUP_CF_TYPE,
	MULTI_GROUP_CF_TYPE,
	...OPTION_FIELD_TYPES,
	...VERSION_FIELD_TYPES,
	...USER_FIELD_TYPES,
]);

export const NUMBER_FIELD_TYPES = new Set<string>([NUMBER_CF_TYPE, STORY_POINT_ESTIMATE_CF_TYPE]);

export const SUPPORTED_FIELD_TYPES = new Set<string>([
	...STRING_FIELD_TYPES,
	...NUMBER_FIELD_TYPES,
]);

export const isOptionFieldType = (fieldType: string): fieldType is OptionFieldType =>
	OPTION_FIELD_TYPES.has(fieldType);

export const isVersionFieldType = (fieldType: string): fieldType is VersionFieldType =>
	VERSION_FIELD_TYPES.has(fieldType);

export const isUserFieldType = (fieldType: string): fieldType is UserFieldType =>
	USER_FIELD_TYPES.has(fieldType);

export const isCreateIssueSupportedForFieldType = (
	fieldType?: string | null,
): fieldType is StringFieldType | NumberFieldType =>
	Boolean(fieldType && SUPPORTED_FIELD_TYPES.has(fieldType));

export const convertFieldToStatusGICInput = ({
	fieldValue,
}: StringGroupContextField | GroupContextFieldOld) => {
	if (fieldValue === null) {
		return null;
	}

	return { status: { statusId: fieldValue } };
};

export const convertFieldToGICAdditionalFieldsPayload = (
	groupContextFieldInput: GroupContextField,
): { [key: string]: AdditionalFieldValue } | null => {
	const { fieldType, fieldValue, fieldId } = groupContextFieldInput;
	if (fieldValue === null) {
		return null;
	}

	// Note: excluding status type and issue type as they're always shown in GIC
	switch (fieldType) {
		case PRIORITY_TYPE:
		case ASSIGNEE_TYPE:
		case REPORTER_TYPE:
		case USER_CF_TYPE:
			return {
				[fieldId]: {
					id: fieldValue,
				},
			};
		case STORY_POINT_ESTIMATE_CF_TYPE:
		case NUMBER_CF_TYPE:
			if (typeof fieldValue === 'number' && fg('group_by_any_fields_technical')) {
				return { [fieldId]: fieldValue };
			}
			return {
				[fieldId]: {
					id: fieldId,
					value: fieldValue,
				},
			};
		case SPRINT_TYPE:
			return {
				[fieldId]: {
					id: fieldId,
					value: fieldValue,
				},
			};
		case LABELS_TYPE:
			return {
				[fieldId]: [fieldValue],
			};
		case VERSION_CF_TYPE:
		case AFFECTS_VERSIONS_TYPE:
		case FIX_VERSIONS_TYPE:
		case SELECT_CF_TYPE:
		case RADIO_BUTTONS_CF_TYPE:
		case CATEGORY_TYPE:
			return {
				[fieldId]: {
					id: fieldValue,
					value: fieldValue,
				},
			};
		case MULTI_SELECT_CF_TYPE:
		case MULTI_CHECKBOXES_CF_TYPE:
		case MULTI_VERSION_CF_TYPE:
			return {
				[fieldId]: [{ id: fieldValue }],
			};
		default:
			// note: this code path should never be reached following the cleanup of the group_by_any_fields_technical flag
			// as the typing on GroupContextField will only support supported field types
			return null;
	}
};

export const convertFieldToIssueCreateInput = ({
	fieldType,
	fieldValue,
	fieldId,
}: GroupContextField): JiraIssueFieldsInput | null => {
	if (fieldValue === null) {
		return null;
	}

	// Note: excluding issue type as it's supported directly via inline issue create
	switch (fieldType) {
		case PRIORITY_TYPE:
			return { priority: { priorityId: fieldValue } };
		case ASSIGNEE_TYPE:
		case USER_CF_TYPE:
		case REPORTER_TYPE:
			return {
				singleSelectUserPickerFields: [{ fieldId, user: { accountId: fieldValue } }],
			};
		case STATUS_TYPE:
			return { status: { statusId: fieldValue } };
		case STORY_POINT_ESTIMATE_CF_TYPE:
		case NUMBER_CF_TYPE:
			if (fg('group_by_any_fields_technical')) {
				// this check will be removed as part of the fg cleanup logic, as once cleaned up TS will know that fieldValue is a number at this point
				if (typeof fieldValue === 'number') {
					return { numberFields: [{ fieldId, value: fieldValue }] };
				}
			}

			// TODO: JSC-2109
			// there is currently a type issue where the `mergeWith` usage in
			// src/packages/platform/ui/issue-table/issue-table/src/services/issue-create-mutation/utils/to-issue-create-input.tsx
			// is hiding an implicit casting this from a string to a number. We will update the GroupContextField type to have number fields defined as a number.
			// Note even though this type doesn't match the API type, it still accepts a string, and is the current behaviour.
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return { numberFields: [{ fieldId, value: fieldValue as unknown as number }] };
		case CATEGORY_TYPE:
		case SELECT_CF_TYPE:
		case RADIO_BUTTONS_CF_TYPE:
			return {
				singleSelectFields: [
					{
						fieldId,
						option: { id: fieldValue, optionId: fieldValue },
					},
				],
			};
		case MULTI_SELECT_CF_TYPE:
		case MULTI_CHECKBOXES_CF_TYPE:
			return {
				multipleSelectFields: [
					{
						fieldId,
						options: [{ id: fieldValue, optionId: fieldValue }],
					},
				],
			};
		case VERSION_CF_TYPE:
			return {
				singleVersionPickerFields: [{ fieldId, version: { versionId: fieldValue } }],
			};
		case AFFECTS_VERSIONS_TYPE:
		case FIX_VERSIONS_TYPE:
		case MULTI_VERSION_CF_TYPE:
			return {
				multipleVersionPickerFields: [{ fieldId, versions: [{ versionId: fieldValue }] }],
			};
		case SPRINT_TYPE:
			return { sprintsField: { fieldId, sprints: [{ sprintId: fieldValue }] } };
		case LABELS_TYPE:
			return {
				labelsFields: [{ fieldId, labels: [{ name: fieldValue }] }],
			};
		case GROUP_CF_TYPE:
			return {
				singleGroupPickerFields: [
					{
						fieldId,
						group: {
							groupName: fieldValue,
						},
					},
				],
			};
		case MULTI_GROUP_CF_TYPE:
			return {
				multipleGroupPickerFields: [
					{
						fieldId,
						groups: [{ groupName: fieldValue }],
					},
				],
			};
		default:
			// note: this code path should never be reached following the cleanup of the group_by_any_fields_technical flag
			// as the typing on GroupContextField will only support supported field types
			return null;
	}
};

export const toOptimisticField = (
	issueId: string,
	{ fieldId, fieldType, optimisticResponse }: GroupContextField,
): OptimisticFieldSet => {
	// Note: excluding issue type as it's supported separately
	switch (optimisticResponse?.type) {
		case PRIORITY_TYPE:
			return toOptimisticPriority({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case ASSIGNEE_TYPE:
		case USER_CF_TYPE:
		case REPORTER_TYPE:
			return toOptimisticUser({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case STATUS_TYPE:
			return toOptimisticStatus({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case CATEGORY_TYPE:
		case SELECT_CF_TYPE:
			return toOptimisticSingleSelect({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case RADIO_BUTTONS_CF_TYPE:
			return toOptimisticRadioSelect({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case MULTI_SELECT_CF_TYPE:
			return toOptimisticMultiSelect({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case MULTI_CHECKBOXES_CF_TYPE:
			return toOptimisticCheckbox({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case SPRINT_TYPE:
			return toOptimisticSprint({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case NUMBER_CF_TYPE:
			return toOptimisticNumber({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case LABELS_TYPE:
			return toOptimisticLabels({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case MULTI_GROUP_CF_TYPE:
			return toOptimisticMultiGroup({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		// Note: Excluding VERSION_CF_TYPE as it's a fallback field.
		case MULTI_VERSION_CF_TYPE:
		case AFFECTS_VERSIONS_TYPE:
		case FIX_VERSIONS_TYPE:
			return toOptimisticMultiVersion({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		default:
			return null;
	}
};
